import Vue from 'vue'
import axios from 'axios'
import store from '@/config/store'
import { urlApi, userKey, applyAuthToStore } from '@/config/global'
import '@/config/loading'

const BUFFER_MINUTES = 5

let isRefreshing = false
let refreshQueue = []

var loading = null
var cont = 0

function isAuthRequest(url) {
    if (!url) return false
    return url.includes('/Auth/signin') || url.includes('/Auth/refresh')
}

function getStoredUser() {
    const user = store.getters.getUser
    if (user) return user
    const json = localStorage.getItem(userKey)
    return json ? JSON.parse(json) : null
}

function isTokenExpired(user) {
    if (!user || !user.expiration) return true
    const expiration = new Date(user.expiration.replace(' ', 'T'))
    const buffer = BUFFER_MINUTES * 60 * 1000
    return Date.now() >= expiration.getTime() - buffer
}

function applyRefreshResult(data) {
    applyAuthToStore(store, data)
    return data.token
}

async function refreshToken() {
    const userData = getStoredUser()
    if (!userData) throw new Error('No user data')

    const res = await axios.post(`${urlApi}/Auth/refresh`, userData, { showLoading: true })
    if (res.data && res.data.token && res.data.token.authenticated) {
        return applyRefreshResult(res.data)
    }
    throw new Error('Refresh failed')
}

function logout() {
    localStorage.removeItem(userKey)
    store.commit('setUser', null)
    window.location = '/Login'
}

async function waitForRefresh() {
    await new Promise(resolve => refreshQueue.push(resolve))
}

function resolveRefreshQueue() {
    refreshQueue.forEach(resolve => resolve())
    refreshQueue = []
}

async function ensureValidToken(force = false) {
    const user = getStoredUser()
    if (!user) return
    if (!force && !isTokenExpired(user)) return

    if (isRefreshing) {
        await waitForRefresh()
        return
    }

    isRefreshing = true
    try {
        await refreshToken()
        resolveRefreshQueue()
    } catch (err) {
        resolveRefreshQueue()
        logout()
        throw err
    } finally {
        isRefreshing = false
    }
}

function showLoadingIfNeeded(req) {
    // eslint-disable-next-line
    if (req.showLoading == undefined || req.showLoading) {
        if (cont == 0) {
            loading = Vue.$loading.show({})
        }
        cont++
    }
}

function hideLoading() {
    if (cont == 1) {
        cont = 0
        loading.hide()
        loading = null
    } else if (cont > 0) {
        cont--
    }
}

const successRequest = async req => {
    if (!isAuthRequest(req.url)) {
        await ensureValidToken()
        const currentUser = store.getters.getUser
        if (currentUser && currentUser.accessToken) {
            req.headers = req.headers || {}
            req.headers.Authorization = `Bearer ${currentUser.accessToken}`
        }
    }

    showLoadingIfNeeded(req)
    return req
}

const errorRequest = err => {
    hideLoading()
    return Promise.reject(err)
}

axios.interceptors.request.use(successRequest, errorRequest)

const successResponse = res => {
    hideLoading()
    return res
}

const errorResponse = async err => {
    hideLoading()

    const originalRequest = err.config

    if (err.response && err.response.status === 401 && originalRequest && !originalRequest._retry && !isAuthRequest(originalRequest.url)) {
        originalRequest._retry = true
        try {
            await ensureValidToken(true)
            const currentUser = store.getters.getUser
            originalRequest.headers.Authorization = `Bearer ${currentUser.accessToken}`
            return axios(originalRequest)
        } catch (refreshErr) {
            return Promise.reject(refreshErr)
        }
    }

    if (err.response && err.response.status === 401 && !isAuthRequest(originalRequest && originalRequest.url)) {
        logout()
    }

    return Promise.reject(err)
}

axios.interceptors.response.use(successResponse, errorResponse)

Vue.use({
    install(Vue) {
        Vue.prototype.$http = axios.create({
            baseURL: urlApi,
        })
    }
})
